import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useDispatch } from 'react-redux'
import { useMatch, useNavigate } from 'react-router'
import { Route, Routes } from 'react-router-dom'
import { LogAccessEvent } from '../../../api/events'
import mf from '../../../assets/img/mf.jpg'
import { useDebounce } from '../../../hooks/Debounce'
import { useMountEffect } from '../../../hooks/MountEffect'
import { useTypedSelector } from '../../../redux/reducers'
import {
  getAccountDetails,
  getConnectionStatus,
  getFeatureUsage,
  getHostStatus,
  getPmitcSessionLogoutLink,
  getPmitcSessionName,
  getPmitcSessionWindow,
  getSystemHealth,
  getSystemInfo,
  isDemo,
} from '../../../redux/selectors'
import { clearHost } from '../../../redux/slideout'
import AuthRole from '../../../role'
import ConfirmationModal from '../../ConfirmationModal/ConfirmationModal'
import { ConnectionStatus, EditCustomerFunction, HostData, HostDataUtil, HostStatus } from '../../Customer/Data'
import { managePmitcCustomer } from '../../CustomersComfortView/Pmitc/Manage'
import { ExclaimIcon, RemoteManageIcon } from '../../Icons/Icons'
import { LinkData } from '../../MfNg/Access/Data'
import PmitcSessionModal from '../../Pmitc/PmitcSessionModal'
import RouteTabLink from '../../Tab/RouteTabLink'
import Toaster from '../../Toaster'
import { Note } from '../Note'
import { HostSlideoutTab } from './Data'
import { EventLogTab } from './EventLogTab'
import { HeaderSpecs } from './HeaderSpecs'
import { MfDevicesTab } from './MfDevicesTab'
import { MfNgAccountTab } from './MfNgAccountTab'
import { MfNgFeatureUsageTab } from './MfNgFeatureUsageTab'
import { MfNgRemoteMonitoringDisabledTab } from './MfNgRemoteMonitoringDisabledTab'
import { MfNgSystemHealthTab } from './MfNgSystemHealthTab'
import { PmitcAccountTab } from './PmitcAccountTab'
import { PmitcFeatureUsageTab } from './PmitcFeatureUsageTab'
import { PmitcServiceHealthTab } from './PmitcServiceHealthTab'

interface Props {
  host: HostData
  teamID: string
  userName: string
  editCustomer: EditCustomerFunction
  startTab?: HostSlideoutTab
}

export const HostSlideout: FunctionComponent<Props> = ({ host, teamID, userName, editCustomer, startTab }) => {
  const account = useTypedSelector(getAccountDetails(host.id))
  const systemInfo = useTypedSelector(getSystemInfo(host.id))
  const systemHealth = useTypedSelector(getSystemHealth(host.id))
  const hostStatus = useTypedSelector(getHostStatus(host.id))
  const featureUsage = useTypedSelector(getFeatureUsage(host.id))
  const connectionStatus = useTypedSelector(getConnectionStatus(host.id))
  const demoMode = useTypedSelector(isDemo)
  const canRemoteManage = AuthRole.hasPermission('remoteManage')
  const productName = HostDataUtil.getProductText(systemInfo?.product ?? host.product)

  const remoteMfNgEnabled = canRemoteManage && connectionStatus === ConnectionStatus.Live
  const manageMfDevicesEnabled = canRemoteManage && connectionStatus !== ConnectionStatus.Disconnected
  const remoteMfMonitoringDisabled = hostStatus === HostStatus.MonitoringDisabled
  const [remoteMfNgSession, setRemoteMfNgSession] = useState(false)
  const [remoteMfDevice, setRemoteMfDevice] = useState(false)
  const [mfDevices, setMfDevices] = useState<LinkData[]>([])
  const [mfDeviceSearch, setMfDeviceSearch] = useState('')
  const [mfNgSessionTime, setMfNgSessionTime] = useState(0)
  const [showEndMfNgSessionConfirmModal, setShowEndMfNgSessionConfirmModal] = useState(false)
  const [closeOnEndMfNgSession, setCloseOnEndMfNgSession] = useState(false)
  const remoteMfNgView = remoteMfNgSession || remoteMfDevice
  const debouncedMfDeviceSearchFilter = useDebounce(mfDeviceSearch, 500)

  const isPmitc = HostDataUtil.isPmitc(host.product)
  const canManagePmitc = canRemoteManage && connectionStatus !== ConnectionStatus.Disabled
  const existingPmitcSessionLogoutLink = useTypedSelector(getPmitcSessionLogoutLink)
  const existingPmitcSessionName = useTypedSelector(getPmitcSessionName)
  const existingPmitcSessionWindow = useTypedSelector(getPmitcSessionWindow)
  const [showEndPmitcSessionConfirmModal, setShowEndPmitcSessionConfirmModal] = useState(false)

  const filteredMfDevices = mfDevices.filter(d => {
    let searchTerm = debouncedMfDeviceSearchFilter.trim().toLowerCase()
    if (searchTerm) {
      return d.name.toLowerCase().includes(searchTerm) || (d.subName?.toLowerCase().includes(searchTerm) ?? false)
    }

    return true
  })

  const ref = useRef<HTMLHtmlElement>(null)
  const overlay = useRef<HTMLDivElement>(null)

  const match = useMatch('/my-customers/:subpath/*')
  const subPath = match?.params.subpath ?? 'list'
  const navigateTo = useNavigate()

  const dispatch = useDispatch()

  const escapeListener = (e: KeyboardEvent) => {
    if (e.key === 'Escape' || e.keyCode === 27) exitSlideout()
  }

  useMountEffect(() => {
    overlay.current?.addEventListener('keyup', escapeListener)

    navigateTo('/my-customers/' + subPath + '/' + (startTab ?? HostSlideoutTab.Account))

    return () => overlay.current?.removeEventListener('keyup', escapeListener)
  })

  const close = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    exitSlideout()
  }

  const exitSlideout = () => {
    if (remoteMfNgSession) {
      setCloseOnEndMfNgSession(true)
      setShowEndMfNgSessionConfirmModal(true)
    } else {
      doClose()
    }
  }

  const doClose = () => {
    if (ref.current) ref.current.classList.remove('visible')

    setTimeout(() => {
      dispatch(clearHost())
      navigateTo('/my-customers/' + subPath)
    }, 300)
  }

  const openRemoteMfNgSession = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    LogAccessEvent(teamID, host.id, userName, productName)
    setRemoteMfNgSession(true)
  }

  const handleEndMfNgSession = () => {
    setCloseOnEndMfNgSession(false)
    setShowEndMfNgSessionConfirmModal(true)
  }

  const endRemoteMfNgSession = () => {
    setShowEndMfNgSessionConfirmModal(false)
    setRemoteMfNgSession(false)
    if (closeOnEndMfNgSession) {
      doClose()
    }
  }

  const manageMfDevices = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    axios
      .get<any>('/api/teams/' + teamID + '/hosts/' + host.id + '/devices')
      .then(resp => {
        const mfds: LinkData[] = []
        resp.data.forEach((dev: any) => {
          mfds.push({
            name: dev.deviceName,
            subName: dev.deviceType,
            url: dev.remoteURL,
          })
        })
        setMfDevices(mfds)
        setRemoteMfDevice(true)
      })
      .catch(_ => Toaster.notifyFailure('Unable to load device list.'))
  }

  const handleMfDeviceBack = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setRemoteMfDevice(false)
  }

  useEffect(() => {
    if (remoteMfNgSession) {
      const interval = setInterval(() => setMfNgSessionTime(seconds => seconds + 1), 1000)
      return () => clearInterval(interval)
    } else {
      setMfNgSessionTime(0)
    }
  }, [remoteMfNgSession])

  useMountEffect(() => {
    if (ref.current) {
      ref.current.classList.add('visible')
    }

    if (overlay.current) {
      const overlayRef = overlay.current
      setTimeout(() => (overlayRef.style.opacity = '1'), 300)
      overlay.current.style.zIndex = '9'
    }
  })

  const remoteMfNavBar = (
    <div className='rm-pc'>
      <i className='icon twenty'>
        <RemoteManageIcon />
      </i>
      <div className='row flex flex-nowrap'>
        <div className='col customer-product'>
          <h4>{productName}</h4>
          <small>v{systemInfo?.version ?? account?.paperCutVersion ?? '--'}</small>
        </div>
        <div className='col'>
          {Math.floor(mfNgSessionTime / 60)}m {mfNgSessionTime % 60}s<small>Session time</small>
        </div>
        <div className='rgt col'>
          <a
            href={demoMode ? mf : host.remoteManageLink}
            className='fullscreen'
            target='_blank'
            rel='noopener noreferrer'
          >
            Full screen
          </a>
          <button className='btn secondary sml end' onClick={handleEndMfNgSession}>
            End session
          </button>
        </div>
      </div>
    </div>
  )

  const mfDeviceNavBar = (
    <div className='rm-mfd'>
      <div className='row flex flex-nowrap flex-ver'>
        <div className='col third'>
          <h4>Manage devices {mfDevices.length > 0 && <>({mfDevices.length})</>}</h4>
        </div>
        <div className='col third'>
          <input
            placeholder='Search by name or type'
            type='text'
            value={mfDeviceSearch}
            onChange={e => setMfDeviceSearch(e.currentTarget.value)}
          />
        </div>
        <div className='col rgt'>
          <a href='#' className='btn secondary sml back' onClick={handleMfDeviceBack}>
            Back
          </a>
        </div>
      </div>
    </div>
  )

  const accountTab = () => {
    if (isPmitc) {
      return <PmitcAccountTab teamID={teamID} editCustomer={editCustomer} host={host} />
    }

    return (
      <MfNgAccountTab
        teamID={teamID}
        editCustomer={editCustomer}
        host={host}
        upgradeRequired={HostDataUtil.newVersionRequired(HostDataUtil.getVersion(systemInfo, account))}
        systemInfo={systemInfo}
        account={account}
      />
    )
  }

  const healthTab = () => {
    if (isPmitc) {
      return <PmitcServiceHealthTab />
    }

    if (!remoteMfMonitoringDisabled && systemHealth) {
      return <MfNgSystemHealthTab systemHealth={systemHealth} hostStatus={hostStatus} />
    }

    return <MfNgRemoteMonitoringDisabledTab name={host.name} />
  }

  const featureUsageTab = () => {
    if (isPmitc) {
      return <PmitcFeatureUsageTab product={host.product} />
    }

    if (remoteMfMonitoringDisabled) {
      return <MfNgRemoteMonitoringDisabledTab name={host.name} />
    }

    return <MfNgFeatureUsageTab customerName={host?.name ?? ''} featureUsage={featureUsage} hostStatus={hostStatus} />
  }

  const eventLogTab = () => <EventLogTab teamID={teamID} hostID={host.id} />

  const notesTab = () => (
    <div id='tab-notes'>
      <header className='flex flex-ver'>
        <h2>Notes</h2>
      </header>
      <Note teamID={teamID} hostID={host.id} top={true} />
    </div>
  )

  const handleManagePmitc = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (existingPmitcSessionWindow && !existingPmitcSessionWindow.closed) {
      setShowEndPmitcSessionConfirmModal(true)
    } else {
      managePmitcCustomer(dispatch, teamID, host, userName, demoMode)
    }
  }

  return (
    <>
      <aside ref={ref} className='org'>
        <article className='customer alt'>
          <div className='flex fp'>
            <header>
              <HeaderSpecs host={host} onClose={close} />
              {hostStatus === HostStatus.StaleData && (
                <div className='alert error flex-ver'>
                  <i className='icon'>
                    <ExclaimIcon />
                  </i>
                  <div className='message'>
                    PaperCut Multiverse is having trouble connecting to this customer environment. Check that the
                    PaperCut Multiverse Connector is running as expected.
                  </div>
                </div>
              )}
              <div className='tabs-wrap'>
                <div className={`tabs five new ${remoteMfNgView ? 'remoteopen' : ''}`}>
                  <ul>
                    <RouteTabLink path={`/my-customers/${subPath}/${HostSlideoutTab.Account}`}>Account</RouteTabLink>
                    <RouteTabLink path={`/my-customers/${subPath}/${HostSlideoutTab.SystemHealth}`}>
                      {isPmitc ? 'Service health' : 'System health'}
                    </RouteTabLink>
                    <RouteTabLink path={`/my-customers/${subPath}/${HostSlideoutTab.FeatureUsage}`}>
                      Feature usage
                    </RouteTabLink>
                    <RouteTabLink path={`/my-customers/${subPath}/${HostSlideoutTab.EventLog}`}>Event log</RouteTabLink>
                    <RouteTabLink path={`/my-customers/${subPath}/${HostSlideoutTab.Notes}`}>Notes</RouteTabLink>
                  </ul>
                  <div className='rgt btn-group'>
                    {isPmitc && (
                      <a
                        className={`btn rm sml outline inv ${canManagePmitc ? '' : 'disabled'}`}
                        href='#'
                        onClick={handleManagePmitc}
                      >
                        Manage
                      </a>
                    )}

                    {!isPmitc && (
                      <>
                        <a
                          className={`btn rm sml outline inv ${remoteMfNgEnabled ? '' : 'disabled'}`}
                          href='#'
                          onClick={openRemoteMfNgSession}
                        >
                          Manage PaperCut
                        </a>
                        <a
                          className={`btn rm sml outline inv ${manageMfDevicesEnabled ? '' : 'disabled'}`}
                          href='#'
                          onClick={manageMfDevices}
                        >
                          Manage devices
                        </a>
                      </>
                    )}
                  </div>
                  <div className='navbar'>
                    {remoteMfNgSession && remoteMfNavBar}
                    {remoteMfDevice && mfDeviceNavBar}
                  </div>
                </div>
              </div>
            </header>
          </div>
        </article>

        {!remoteMfNgView && (
          <div className='scroll'>
            <Routes>
              <Route path={`/my-customers/${subPath}/${HostSlideoutTab.Account}`} element={accountTab()} />
              <Route path={`/my-customers/${subPath}/${HostSlideoutTab.SystemHealth}`} element={healthTab()} />
              <Route path={`/my-customers/${subPath}/${HostSlideoutTab.FeatureUsage}`} element={featureUsageTab()} />
              <Route path={`/my-customers/${subPath}/${HostSlideoutTab.EventLog}`} element={eventLogTab()} />
              <Route path={`/my-customers/${subPath}/${HostSlideoutTab.Notes}`} element={notesTab()} />
            </Routes>
          </div>
        )}
        {remoteMfNgSession && (
          <div className='scroll'>
            <div id='tab-remote' className='tab'>
              <div className='window'>
                {demoMode ? <img src={mf} alt='mf' /> : <iframe title={host.name} src={host.remoteManageLink} />}
              </div>
            </div>
          </div>
        )}
        {remoteMfDevice && (
          <MfDevicesTab
            teamID={teamID}
            hostID={host.id}
            devices={filteredMfDevices}
            allDevicesCount={mfDevices.length}
          />
        )}
        {showEndMfNgSessionConfirmModal && (
          <ConfirmationModal
            title='Are you sure you want to end your session?'
            message={`Session time: ${Math.floor(mfNgSessionTime / 60)}m ${mfNgSessionTime % 60}s`}
            onClose={() => setShowEndMfNgSessionConfirmModal(false)}
            onYes={endRemoteMfNgSession}
            cannotUndoWarning={false}
          />
        )}
        {showEndPmitcSessionConfirmModal && (
          <PmitcSessionModal
            customer={existingPmitcSessionName}
            onClose={() => setShowEndPmitcSessionConfirmModal(false)}
            onYes={() => {
              if (existingPmitcSessionWindow && existingPmitcSessionLogoutLink) {
                existingPmitcSessionWindow.location.href = existingPmitcSessionLogoutLink
              }

              managePmitcCustomer(dispatch, teamID, host, userName, demoMode)
            }}
          />
        )}
      </aside>
      <div ref={overlay} className='overlay' onClick={exitSlideout} />
    </>
  )
}
